<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import Close from '../../icons/Close.svelte';
  import {
    AppState,
    SetIconsBackground,
    SetNotification
  } from '../../../etc/stores';
  import MessagePopup from '../../misc/MessagePopup.svelte';
  import ThemedIcon from '../../misc/ThemedIcon.svelte';
  import FreeCoinsCampaignHeader from '../../icons/FreeCoinsCampaignHeader.svelte';
  import Button from '../../form/Button.svelte';
  import Link from '../../misc/Link.svelte';
  import { Container } from '../../../services/container';
  import { Auth } from '../../../services/auth';
  import { Logger } from '../../../services/logger';
  import { ReferralProgram } from '../../../services/referral-program';
  import Analytics from '../../../services/analytics';
  import {
    AUTH_STORAGE_FLAGS,
    SessionManager
  } from '../../../services/session-manager';
  import PoweredBy from '../../misc/PoweredBy.svelte';
  import { replaceVariables } from '../../../i18n';
  import RewardCoin from '../../misc/RewardCoin.svelte';
  import Heading from '../../typography/Heading.svelte';
  import Text from '../../typography/Text.svelte';
  import AuthConnector from '../../misc/AuthConnector.svelte';

  let setLoading;
  let handleConnectCustomer;

  const session = Container.get(SessionManager) as SessionManager;
  const logger = Container.get(Logger).context('ReferredCoin.svelte') as Logger;
  const referralProgram = Container.get(ReferralProgram);
  const auth = Container.get(Auth);

  const { businessReferral, referringCustomer } =
    $AppState.referralReferredState;

  const discount = Number(businessReferral.referredDiscount);

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`referred-coin-popup.${text}`), values);
  };

  const couponData = {
    formattedMoneyAmount: `${discount}%`,
    moneyValue: discount * 100,
    code: 'TWM*******',
    status: 'active',
    type: 'referral'
  };

  const closeMessage = () => {
    SetNotification('');
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        connectingAccount: false
      }
    }));

    setTimeout(() => {
      if (!$AppState.website.popoverOpen) {
        SetNotification('remindReferred');
      }
    }, 2000);
  };

  const howItWorks = () => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        popoverOpen: true,
        popoverRouteOnOpen: {
          route: '/how-it-works',
          navigationOptions: {
            state: {
              onBackButton: () => {
                AppState.update(state => ({
                  ...state,
                  website: {
                    ...state.website,
                    popoverOpen: false
                  }
                }));
              }
            }
          }
        }
      }
    }));
  };

  const onGAEventClaimSuccess = () => {
    Analytics.logEvent('click-claim-coupon-success', 'referred-state');
  };

  const redeemCoupon = async () => {
    if (setLoading) setLoading(true);

    const claimAction = async () => {
      const coupon = await referralProgram.claimReferredCoupon();
      onGAEventClaimSuccess();
      session.remove(AUTH_STORAGE_FLAGS.referral);
      SetNotification('paymentCoinSaved', true, {
        couponData: coupon,
        couponVariant: 'referral'
      });
      window.history.replaceState({}, document.title, '/');
    };

    try {
      await claimAction();
    } catch (err) {
      if (
        err.response.status === 400 &&
        err.response.data.slug ===
          'exceptions.businessReferralCampaign.alreadyParticipated'
      ) {
        SetNotification('referredAlreadyJoined', true);
        window.history.replaceState({}, document.title, '/');
      } else if (
        $AppState.customer.id ===
        $AppState.referralReferredState.referringCustomer.id
      ) {
        SetNotification('referYourselfError', true);
      } else {
        logger.error(err);
        SetNotification('paymentCoinUnexpectedError', true, {
          claimAction,
          description: $_('refer_unexpected_error_popup.description')
        });
      }

      session.remove(AUTH_STORAGE_FLAGS.referral);
    }
  };

  const onAfterConnectCustomer = async next => {
    if (next.route === '/dashboard') {
      await redeemCoupon();
    }
  };

  const handleSubmit = async () => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        authFlow: 'referred-redeem'
      }
    }));
    session.set(AUTH_STORAGE_FLAGS.referral, location.search);

    Analytics.logEvent('click-claim-coupon', 'referred-state');

    if ($AppState.auth.connected) {
      redeemCoupon();
    } else if (await auth.isAuthenticatedOnWebsite()) {
      handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      auth.openLoginModal();
    }
  };

  const triggerConnectCustomer = () => {
    SetIconsBackground('light-without-background');

    if ($AppState.auth.connected) {
      redeemCoupon();
    } else {
      handleConnectCustomer();
    }
  };

  onMount(() => {
    if (!$AppState.website.connectingAccount)
      Analytics.logEvent('render', 'referred-state');
  });
</script>

<AuthConnector
  {triggerConnectCustomer}
  {onAfterConnectCustomer}
  loggerContext="ReferredCoin.svelte"
  bind:handleConnectCustomer
  bind:setLoading
  let:loading
>
  <MessagePopup>
    <div class="referred-coin-available--wrapper">
      <div class="referred-coin-available--close" on:click={closeMessage}>
        <ThemedIcon theme="light-without-background">
          <Close />
        </ThemedIcon>
      </div>
      <div class="referred-coin-available--top">
        <div class="referred-coin-available--header">
          <FreeCoinsCampaignHeader />
        </div>
        <div class="referred-coin-available--coin">
          <RewardCoin
            size="120"
            value={couponData.moneyValue / 100}
            type="percentage"
          />
          <Heading type="h2">
            {t('title', { referringFirstName: referringCustomer.name })}
          </Heading>
        </div>
      </div>
      <div class="referred-coin-available--bottom">
        <Text type="default" textAlign="center">
          {t('description')}
        </Text>
        <Button on:click={handleSubmit} {loading}>
          <span>{t('join')}</span>
        </Button>
        <div class="referred-coin-available--bottom--link">
          <Link fontSize="14px" lineHeight="20px" on:click={howItWorks}>
            {t('how_it_works')}
          </Link>
        </div>
      </div>
      <PoweredBy />
    </div>
  </MessagePopup>
</AuthConnector>

<style lang="scss">
  @use './styles.scss' as *;

  .referred-coin-available {
    &--wrapper {
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      width: 100%;
    }

    &--close {
      position: absolute;
      top: 16px;
      right: 16px;
      cursor: pointer;
    }

    &--header {
      width: 100%;
      height: 64px;
      display: flex;
      justify-content: center;
      align-items: flex-start;
    }

    &--top {
      background-color: var(--twism-theme-freeCoinsCampaign-background);
      width: 100%;
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      padding: 0 16px 0;
      box-sizing: border-box;
    }

    &--bottom {
      width: 100%;
      box-sizing: border-box;
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      padding: 0 24px 44px;

      @media screen and (min-width: 365px) and (max-width: 468px) {
        max-width: 332px;
      }

      :global(p) {
        margin: 28px 0;
      }

      span {
        font-weight: 600;
        font-size: 15px;
        line-height: 16px;
        color: #fff;
        margin-left: 8px;
      }

      &--link {
        margin-top: 24px;
      }

      @media screen and (max-height: 620px) {
        &--link {
          margin-top: 16px;
        }

        padding-bottom: 16px;
      }
    }

    &--coin {
      display: flex;
      align-items: center;
      margin-bottom: 40px;

      :global(h2) {
        margin-left: 16px;
        line-height: 32px !important;
        max-width: 176px;
      }
    }
  }
</style>
